import { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getTrainingById, trainings } from "../data/trainings";

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// Content'i `## Başlık` ve paragraf/liste bloklarına parse eder
function parseContent(content) {
  if (!content) return [];
  const blocks = content.trim().split(/\n\s*\n/);
  return blocks.map((block) => {
    const trimmed = block.trim();
    if (trimmed.startsWith("## ")) {
      return { type: "heading", text: trimmed.replace(/^##\s+/, "") };
    }
    const lines = trimmed.split("\n");
    const bulletPattern = /^[•*✨✔❌📌]\s+/;
    if (lines.every((l) => bulletPattern.test(l.trim()))) {
      return {
        type: "list",
        items: lines.map((l) => {
          const m = l.trim().match(/^([•*✨✔❌📌])\s+(.*)$/);
          return m ? { icon: m[1], text: m[2] } : { icon: "•", text: l };
        }),
      };
    }
    return { type: "paragraph", text: trimmed };
  });
}

export default function EgitimDetail() {
  const { id } = useParams();
  const training = getTrainingById(id);
  const sections = useMemo(
    () => (training ? parseContent(training.content) : []),
    [training]
  );

  useEffect(() => {
    if (!training) return;
    const url = `https://aysunseremet.com/egitimler/${training.id}`;
    document.title = `${training.title} — Uzm. Klinik Psikolog Aysun Şeremet`;
    setMeta("description", training.description);
    setMeta("og:title", training.title, "property");
    setMeta("og:description", training.description, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", "article", "property");
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) {
      c = document.createElement("link");
      c.rel = "canonical";
      document.head.appendChild(c);
    }
    c.href = url;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [training]);

  if (!training) return <Navigate to="/egitimler" replace />;

  const currentIndex = trainings.findIndex((t) => t.id === training.id);
  const prev = currentIndex > 0 ? trainings[currentIndex - 1] : null;
  const next =
    currentIndex < trainings.length - 1 ? trainings[currentIndex + 1] : null;

  // Renderle — renk pembe eksen
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-white"
    >
      <div className="mx-auto max-w-3xl px-5 pt-10 pb-20 sm:px-6">
        {/* Geri link */}
        <Link
          to="/egitimler"
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-pink-600 transition"
        >
          <span aria-hidden>←</span>
          Tüm Eğitimler
        </Link>

        {/* Header */}
        <header className="mt-10 border-b border-gray-200 pb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-pink-600">
            Webinar
          </p>

          <h1 className="mt-4 text-3xl font-bold leading-tight text-gray-900 sm:text-4xl">
            {training.title}
          </h1>

          {training.subtitle && (
            <p className="mt-4 text-lg italic text-gray-500 sm:text-xl">
              {training.subtitle}
            </p>
          )}

          <div className="mt-6 h-px w-12 bg-pink-400" />

          {/* Yazar */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-100 text-sm font-bold text-pink-600">
              AŞ
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Aysun ŞEREMET
              </p>
              <p className="text-xs text-gray-500">Uzman Klinik Psikolog</p>
            </div>
          </div>

          {/* Meta */}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
            {training.duration && (
              <span className="inline-flex items-center gap-2">
                <svg
                  className="h-4 w-4 text-pink-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" strokeLinecap="round" />
                </svg>
                {training.duration}
              </span>
            )}
            <span className="inline-flex items-center gap-2">
              <svg
                className="h-4 w-4 text-pink-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="5" width="18" height="12" rx="2" />
                <path d="M8 21h8" strokeLinecap="round" />
              </svg>
              Online Katılım
            </span>
          </div>
        </header>

        {/* İçerik */}
        <article className="mt-10 space-y-6">
          {sections.map((s, i) => {
            if (s.type === "heading") {
              return (
                <h2
                  key={i}
                  className="mt-10 text-2xl font-semibold text-gray-900 sm:text-[1.65rem]"
                >
                  {s.text}
                </h2>
              );
            }
            if (s.type === "list") {
              return (
                <ul key={i} className="space-y-2.5 pl-1">
                  {s.items.map((it, j) => {
                    const isSpecial = ["✨", "✔", "❌", "📌"].includes(it.icon);
                    return (
                      <li
                        key={j}
                        className="flex items-start gap-3 text-[15px] leading-relaxed text-gray-700"
                      >
                        {isSpecial ? (
                          <span className="mt-0.5 text-sm leading-none shrink-0 select-none">
                            {it.icon}
                          </span>
                        ) : (
                          <span
                            className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-400"
                            aria-hidden
                          />
                        )}
                        <span>{it.text}</span>
                      </li>
                    );
                  })}
                </ul>
              );
            }
            return (
              <p
                key={i}
                className="whitespace-pre-line text-[15px] leading-[1.85] text-gray-700 sm:text-base"
              >
                {s.text}
              </p>
            );
          })}
        </article>

        {/* Audience */}
        {training.audience && (
          <div className="mt-12 rounded-2xl bg-pink-50 px-6 py-5 ring-1 ring-pink-100">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pink-600">
              Kimler İçin Uygun
            </p>
            <p className="mt-2 text-sm leading-relaxed text-gray-700">
              {training.audience}
            </p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 rounded-2xl border border-gray-200 bg-white px-7 py-8">
          <h3 className="text-lg font-semibold text-gray-900 sm:text-xl">
            Bu eğitime katılmak ister misiniz?
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Tarih, ücret ve kontenjan bilgisi için iletişime geçebilirsiniz.
          </p>
          <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
            <a
              href="https://wa.me/905372153407?text=Merhaba,%20eğitim%20hakkında%20bilgi%20almak%20istiyorum."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-pink-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-pink-600"
            >
              WhatsApp
            </a>
            <Link
              to="/iletisim"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 transition hover:ring-pink-300"
            >
              İletişim Formu
            </Link>
            <a
              href="tel:+905372153407"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 transition hover:ring-pink-300"
            >
              0537 215 34 07
            </a>
          </div>
        </div>

        {/* Prev / Next */}
        {(prev || next) && (
          <div className="mt-14 grid grid-cols-1 gap-3 border-t border-gray-200 pt-8 sm:grid-cols-2">
            {prev ? (
              <Link
                to={`/egitimler/${prev.id}`}
                className="group rounded-xl px-4 py-4 transition hover:bg-gray-50"
              >
                <p className="text-[11px] uppercase tracking-wider text-gray-400">
                  ← Önceki
                </p>
                <p className="mt-1.5 text-sm font-medium text-gray-900 group-hover:text-pink-600 line-clamp-2">
                  {prev.title}
                </p>
              </Link>
            ) : (
              <div />
            )}
            {next ? (
              <Link
                to={`/egitimler/${next.id}`}
                className="group rounded-xl px-4 py-4 text-right transition hover:bg-gray-50"
              >
                <p className="text-[11px] uppercase tracking-wider text-gray-400">
                  Sonraki →
                </p>
                <p className="mt-1.5 text-sm font-medium text-gray-900 group-hover:text-pink-600 line-clamp-2">
                  {next.title}
                </p>
              </Link>
            ) : (
              <div />
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
